import type { ConfigValues, PuzzleId } from "./types.ts";

/**
 * Additional key commands not exposed by Puzzle.requestKeys().
 * We add them to the game menu for access on touch devices.
 */
export interface ExtraCommand {
  name: string;
  icon?: string;
  button: number; // for Puzzle.processKey()
}

const keyToButton = (key: string): number => {
  if (key.length !== 1) {
    throw new Error(`Invalid key '${key}'`);
  }
  return key.charCodeAt(0);
};

const ALL_MARKS_COMMAND: ExtraCommand = {
  name: "Fill in marks",
  icon: "marks-all",
  button: keyToButton("M"),
} as const;

const HINT_COMMAND: ExtraCommand = {
  // Reveal one hint
  name: "Hint",
  icon: "hint",
  button: keyToButton("H"),
} as const;

const HINT_MARKS_COMMAND: ExtraCommand = {
  // Add all marks except "obvious" ones (e.g., Unequal)
  name: "Fill in hints",
  icon: "marks-hints",
  button: keyToButton("H"),
} as const;

/**
 * Additional puzzle-specific metadata and functionality
 * that isn't (currently) possible in the C code
 */
export interface PuzzleAugmentations {
  /**
   * Construct a human-readable description of the given puzzle configuration.
   *
   * The implementations here try to follow the style of the existing preset titles
   * for the same puzzle. (Capitalization and punctuation seems to vary quite a bit
   * between puzzles.) Use British spelling to match the existing presets.
   */
  describeConfig?: (config: ConfigValues) => string;

  /**
   * Index of palette color used as background. (Default 0.)
   */
  paletteBgIndex?: number;

  darkMode?: {
    /**
     * Palette indexes that need special handling in automatic dark mode
     * generation. Set to:
     * - `false` to leave the puzzle's light-mode palette color unchanged
     *   in dark mode (e.g., for semantic "black" or "white")
     * - a number to scale the lightness of the calculated dark mode color
     * - an OKLCH color tuple to specify a fixed color
     */
    paletteOverrides?: Record<number, false | number | [number, number, number]>;

    /**
     * Pairs of palette index to swap after automatic dark mode generation.
     * Applied after any paletteOverrides.
     *
     * This is useful for puzzles that use game_mkhighlight to create a 3D
     * effect, where the inverted dark mode lightness results in swapping
     * embossed and inset appearances. (Not all uses of game_mkhighlight
     * should be swapped. E.g., cursor and selection indicators are usually
     * better left as is.)
     */
    paletteSwaps?: [number, number][];
  };

  extraCommands?: ExtraCommand[];

  /**
   * Whether to immediately deliver the LEFT_BUTTON primary button press before
   * waiting to see if it turns into a secondary press through long-press or
   * two-finger-tap detection. When true, if a gesture turns out to be a secondary
   * press, the primary is canceled by sending LEFT_RELEASE outside the puzzle area
   * before delivering RIGHT_BUTTON at the original location.
   *
   * This is needed in Mines so that pressing an uncovered tile highlights adjacent
   * tiles (like left-click-and-hold), but long press on a covered tile toggles its
   * flag (like right-click).
   *
   * This should not be enabled for puzzles LEFT_BUTTON (rather than LEFT_RELEASE)
   * results in a move or other non-cancelable (non-transient) state change.
   *
   * Default: false.
   */
  deliverPrimaryButtonWhileSecondaryDetectionPending?: boolean;
}

export const puzzleAugmentations: Record<PuzzleId, PuzzleAugmentations> = {
  abcd: {
    describeConfig: configFormatter(
      "{width}x{height}, {letters} letters {remove-clues:Easy|Hard}{allow-diagonal-touching:, no diagonal|}",
    ),
    extraCommands: [ALL_MARKS_COMMAND],
  },
  ascent: {
    describeConfig: configFormatter(
      "{width}x{height}{grid-type} {difficulty}{always-show-start-and-end-points}{symmetrical-clues}",
      {
        difficulty: ["Easy", "Normal", "Tricky", "Hard"],
        "grid-type": [" (no diagonals)", "", " Hexagon", " Honeycomb", " Edges"],
        "always-show-start-and-end-points": [", hidden ends", ""], // boolean, on by default
        "symmetrical-clues": ["", ", symmetric"], // boolean, off by default
      },
    ),
  },
  blackbox: {
    describeConfig: configFormatter("{width}x{height}, {no-of-balls}", {
      "no-of-balls": (value) => (String(value) === "1" ? "1 ball" : `${value} balls`),
    }),
    darkMode: {
      paletteSwaps: [[5, 6]], // 3D
    },
  },
  boats: {
    describeConfig: configFormatter(
      "{width}x{height}, size {fleet-size} {difficulty}{remove-numbers}{fleet-configuration}",
      {
        difficulty: ["Easy", "Normal", "Tricky", "Hard"],
        "remove-numbers": ["", ", hidden clues"], // boolean, default off
        // fleet is a comma-separated list of numbers (boats.c removes spaces), default ""
        "fleet-configuration": (value) => (value ? `, fleet ${value}` : ""),
      },
    ),
    darkMode: {
      paletteOverrides: { 4: 0.6 }, // darken the water
    },
  },
  bricks: {
    describeConfig: configFormatter("{width}x{height} {difficulty:Easy|Normal|Tricky}"),
    darkMode: {
      paletteOverrides: { 0: [0.4, 0, 0], 2: [0.6, 0, 0], 4: [0.1, 0, 0] }, // bg, no-brick, brick
    },
  },
  bridges: {
    describeConfig: configFormatter(
      "{width}x{height} {difficulty}{allow-loops}{max-bridges-per-direction}{percentage-of-island-squares}{expansion-factor}",
      {
        // Don't include default values in description
        "allow-loops": (value) => (value ? "" : ", no loops"),
        difficulty: ["easy", "medium", "hard"],
        "max-bridges-per-direction": (value) =>
          value === 0
            ? ", max 1 bridge"
            : value === 1
              ? "" // default max 2 bridges
              : `, max ${Number(value) + 1} bridges`,
        "percentage-of-island-squares": (value) =>
          // Choices "5%", "10%", "15%", "20%", "25%", "30%", default "30%"
          value === 5 ? "" : `, ${5 + 5 * Number(value)}% islands`,
        "expansion-factor": (value) =>
          // Choices "0%", "10%", "20%", ..., "100%", default "10%"
          value === 1 ? "" : `, ${10 * Number(value)}% expansion`,
      },
    ),
    extraCommands: [
      // {name: "Show possible bridges", key: "G"},  // also in prefs, command not as useful
      // HINT_COMMAND, // undocumented, solves entire puzzle
    ],
  },
  clusters: {
    describeConfig: configFormatter("{width}x{height}"),
  },
  crossing: {
    describeConfig: configFormatter("{width}x{height}{symmetric-walls:|, symmetric}"),
  },
  cube: {
    describeConfig: configFormatter(
      // This won't exactly replicate the preset titles, which don't show dimensions.
      // (We'd need to suppress default dimensions, which vary by type of solid.)
      "{type-of-solid:Tetrahedron|Cube|Octahedron|Icosahedron}, {width-top}x{height-bottom}",
    ),
  },
  dominosa: {
    describeConfig: configFormatter(
      "Order {maximum-number-on-dominoes}, {difficulty:Trivial|Basic|Hard|Extreme|Ambiguous}",
    ),
  },
  fifteen: {
    describeConfig: configFormatter("{width}x{height}"),
    darkMode: {
      paletteSwaps: [[2, 3]], // 3D
    },
    extraCommands: [HINT_COMMAND],
  },
  filling: {
    describeConfig: configFormatter("{width}x{height}"),
  },
  flip: {
    describeConfig: configFormatter("{width}x{height} {shape-type:Crosses|Random}"),
  },
  flood: {
    describeConfig: configFormatter(
      "{width}x{height}, {colours} colours{extra-moves-permitted}",
      {
        "extra-moves-permitted": (value) =>
          Number(value) > 0 ? `, ${value} extra moves` : "",
      },
    ),
    darkMode: {
      paletteOverrides: { 1: false }, // keep black lines between regions
      paletteSwaps: [[12, 13]], // 3D
    },
  },
  galaxies: {
    describeConfig: configFormatter(
      "{width}x{height} {difficulty:Normal|Unreasonable}",
    ),
    darkMode: {
      paletteOverrides: { 6: 0.8 }, // edges
    },
    // extraCommands: [HINT_MARKS_COMMAND], // undocumented, fills in all obvious arrows
  },
  group: {
    describeConfig: configFormatter(
      "{grid-size}x{grid-size} {difficulty:Trivial|Normal|Hard|Extreme|Unreasonable}{show-identity:, identity hidden|}",
    ),
    extraCommands: [ALL_MARKS_COMMAND],
  },
  guess: {
    describeConfig: configFormatter(
      "{pegs-per-guess}x{guesses}, {colours} colours{allow-blanks:| + blank}{allow-duplicates:, no duplicates|}",
    ),
    darkMode: {
      paletteOverrides: { 16: false, 17: false }, // black and white pegs
    },
    extraCommands: [HINT_COMMAND],
  },
  inertia: {
    describeConfig: configFormatter("{width}x{height}"),
    darkMode: {
      paletteOverrides: { 6: false }, // black mine
      paletteSwaps: [[2, 3]], // 3D
    },
  },
  keen: {
    describeConfig: configFormatter(
      "{grid-size}x{grid-size} {difficulty:Easy|Normal|Hard|Extreme|Unreasonable}{multiplication-only:|, multiplication only}",
    ),
    extraCommands: [ALL_MARKS_COMMAND],
  },
  lightup: {
    describeConfig: configFormatter(
      "{width}x{height} {difficulty:easy|tricky|hard}{percentage-of-black-squares}{symmetry}",
      {
        // Default black squares is "20". Note value is 5-100, not 0.05-1.0.
        "percentage-of-black-squares": (value) =>
          String(value) === "20" ? "" : `, ${value}% black squares`,
        symmetry: (value, _, config) => {
          // Choices. Presets vary: 7x7 is 4-way rotational, 10x10 and 14x14 are 2-way rotational.
          // 4-way is only valid with square grid.
          const width = Number(config.width);
          const height = Number(config.height);
          const defaultChoice = width === height && width * height < 50 ? 4 : 2;
          const choice = Number(value);
          const symmetry = [
            "no symmetry",
            "2-way mirror",
            "2-way rotational",
            "4-way mirror",
            "4-way rotational",
          ][choice];
          return choice === defaultChoice ? "" : `, ${symmetry}`;
        },
      },
    ),
    darkMode: {
      paletteOverrides: { 2: [0.5, 0, 0], 3: [0.95, 0, 0] }, // black, white
    },
  },
  loopy: {
    describeConfig: configFormatter(
      "{width}x{height} {grid-type} - {difficulty:Easy|Normal|Tricky|Hard}",
      {
        "grid-type": [
          "Squares",
          "Triangular",
          "Honeycomb",
          "Snub-Square",
          "Cairo",
          "Great-Hexagonal",
          "Octagonal",
          "Kites",
          "Floret",
          "Dodecagonal",
          "Great-Dodecagonal",
          "Penrose (kite/dart)",
          "Penrose (rhombs)",
          "Great-Great-Dodecagonal",
          "Kagome",
          "Compass-Dodecagonal",
          "Hats",
          "Spectres",
        ],
      },
    ),
    darkMode: {
      paletteOverrides: { 2: 0.6 }, // darken line-unknown
    },
  },
  magnets: {
    describeConfig: configFormatter(
      "{width}x{height} {difficulty:Easy|Tricky}{strip-clues:|, strip clues}",
    ),
  },
  map: {
    describeConfig: configFormatter(
      "{width}x{height}, {regions} regions, {difficulty:Easy|Normal|Hard|Unreasonable}",
    ),
  },
  mathrax: {
    describeConfig: (config) => {
      const { size } = config;
      const difficulty = ["Easy", "Normal", "Tricky", "Recursive"][
        Number(config.difficulty)
      ];
      const enabledClues: string[] = [];
      const disabledClues: string[] = [];
      for (const clueType of [
        "addition",
        "subtraction",
        "multiplication",
        "division",
        "equality",
        "even-odd",
      ]) {
        (config[`${clueType}-clues`] ? enabledClues : disabledClues).push(clueType);
      }

      // Default is all types enabled; otherwise describe the shorter of the lists
      const cluesDescription =
        disabledClues.length === 0
          ? ""
          : enabledClues.length <= disabledClues.length
            ? `, only ${enabledClues.join("/")}`
            : `, no ${disabledClues.join("/")}`;
      return `${size}x${size} ${difficulty}${cluesDescription}`;
    },
    extraCommands: [ALL_MARKS_COMMAND],
  },
  mines: {
    describeConfig: configFormatter(
      "{width}x{height}, {mines} mines, {grid-type}{ensure-solubility:, risky|}",
      {
        "grid-type": [
          "Squares",
          "Squares wrapping",
          "Honeycomb",
          "Honeycomb wrapping",
          "Octagonal",
          "Triangular",
          "Triangular wrapping",
          "Snub-Square",
          "Cairo",
          "Kites",
          "Great Hexagonal",
          "Kagome",
          "Floret",
          "Dodecagonal",
          "Great-Dodecagonal",
          "Great-Great-Dodecagonal",
          "Compass-Dodecagonal",
          "Penrose Rhombs",
          "Hats",
          "Penrose Kite/Dart",
          "Spectres",
        ],
      },
    ),
    darkMode: {
      paletteOverrides: { 0: [0.2, 0, 0], 10: false, 14: 0.8 }, // bg, black mine, white flag base
      paletteSwaps: [
        [0, 1], // cleared/uncleared background
        [16, 17], // 3D edges
      ],
    },
    deliverPrimaryButtonWhileSecondaryDetectionPending: true,
  },
  mosaic: {
    // Note: settings config lists "Height" before "Width"
    describeConfig: configFormatter("Size: {width}x{height}{aggressive-generation}", {
      "aggressive-generation": (value, _, { width, height }) => {
        // Boolean: on for 3x3, 5x5, 10x10, 15x15, 25x25 presets; off for 50x50.
        // "not recommended for boards larger than, say, 30x30"
        const defaultOption = Number(width) * Number(height) < 30 * 30;
        return value === defaultOption
          ? ""
          : `, ${value ? "slower" : "faster"} generation`;
      },
    }),
    darkMode: {
      paletteOverrides: { 1: 0.6 }, // unmarked tiles
    },
  },
  net: {
    describeConfig: configFormatter(
      "{width}x{height}{walls-wrap-around:| wrapping}{barrier-probability}{ensure-unique-solution:, ambiguous|}",
      {
        // Show barrier % if not default 0
        "barrier-probability": (value) =>
          Number(value) > 0 ? `, ${percentage(value)} barriers` : "",
      },
    ),
    extraCommands: [
      {
        name: "Jumble tile rotations",
        icon: "shuffle",
        button: keyToButton("j"),
      },
    ],
  },
  netslide: {
    describeConfig: ({ width, height, ...config }) => {
      const wrapping = Boolean(config["walls-wrap-around"]);
      const barrierProbability = Number(config["barrier-probability"]);
      // Replicate difficulty logic from preset titles
      let difficulty: string;
      if (!wrapping && barrierProbability === 1) {
        difficulty = " easy";
      } else if (!wrapping && barrierProbability === 0) {
        difficulty = " medium";
      } else if (wrapping && barrierProbability === 0) {
        difficulty = " hard";
      } else {
        // Custom difficulty
        difficulty =
          barrierProbability > 0 ? `, ${percentage(barrierProbability)} barriers` : "";
        if (wrapping) {
          difficulty += ", wrapping";
        }
      }
      const shuffles = Number(config["number-of-shuffling-moves"]);
      return `${width}x${height}${difficulty}${shuffles ? `, ${shuffles} shuffles` : ""}`;
    },
  },
  palisade: {
    describeConfig: configFormatter(
      "{width} x {height}, regions of size {region-size}",
    ),
    darkMode: {
      // Palisade grid/clue/line-yes all share palette index 2
      paletteOverrides: { 3: 0.6 }, // darken line-maybe
    },
  },
  pattern: {
    describeConfig: configFormatter("{width}x{height}"),
    darkMode: {
      paletteOverrides: { 1: false, 2: false }, // white and black squares
    },
  },
  pearl: {
    describeConfig: configFormatter(
      "{width}x{height} {difficulty:Easy|Tricky}{allow-unsoluble:|, ambiguous}",
    ),
    darkMode: {
      paletteOverrides: { 0: 1.15, 3: false, 4: false }, // lighten bg, preserve black, white
    },
    // extraCommands: [HINT], // undocumented, solves entire puzzle
  },
  pegs: {
    // Note: Cross and Octagon currently allow only specific sizes, all covered
    // by presets. (So any params that don't match a preset will be board-type Random.)
    describeConfig: configFormatter(
      "{board-type:Cross|Octagon|Random} {width}x{height}",
    ),
  },
  range: {
    describeConfig: configFormatter("{width}x{height}"),
    // Range reuses palette colors: black = text = grid.
    // Strict inverted dark mode is fine. (You place white squares rather than black.)
    extraCommands: [HINT_COMMAND], // undocumented, reveals one square
  },
  rect: {
    describeConfig: configFormatter(
      "{width}x{height}{expansion-factor}{ensure-unique-solution:, ambiguous|}",
      {
        "expansion-factor": (value) =>
          Number(value) === 0 ? "" : `, ${percentage(value)} expansion`,
      },
    ),
  },
  rome: {
    describeConfig: configFormatter("{width}x{height} {difficulty:Easy|Normal|Tricky}"),
    extraCommands: [ALL_MARKS_COMMAND, HINT_MARKS_COMMAND],
  },
  salad: {
    describeConfig: (config) => {
      const isNumbers = Number(config["game-mode"]) > 0;
      const size = Number(config.size);
      const symbols = Number(config.symbols);
      const difficulty = Number(config.difficulty) === 0 ? "" : " Extreme";
      const range = isNumbers
        ? `1~${symbols}`
        : `A~${String.fromCharCode(65 + symbols - 1)}`;
      return `${isNumbers ? "Numbers" : "Letters"}: ${size}x${size} ${range}${difficulty}`;
    },
    extraCommands: [ALL_MARKS_COMMAND],
  },
  samegame: {
    describeConfig: configFormatter(
      "{width}x{height}, {no-of-colours} colours{ensure-solubility:, ambiguous|}{scoring-system:, alt. scoring|}",
    ),
    darkMode: {
      paletteSwaps: [[12, 13]], // 3D
    },
  },
  seismic: {
    describeConfig: configFormatter(
      "{game-mode:Seismic|Tectonic}: {width}x{height} {difficulty:Easy|Hard}",
    ),
    extraCommands: [ALL_MARKS_COMMAND],
  },
  signpost: {
    describeConfig: configFormatter(
      "{width}x{height}{start-and-end-in-corners:, free ends|}",
    ),
  },
  singles: {
    describeConfig: configFormatter("{width}x{height} {difficulty:Easy|Tricky}"),
  },
  sixteen: {
    describeConfig: configFormatter("{width}x{height}{number-of-shuffling-moves}", {
      "number-of-shuffling-moves": (value) => (value ? `, ${value} shuffles` : ""),
    }),
    darkMode: {
      paletteSwaps: [[2, 3]], // 3D
    },
  },
  slant: {
    describeConfig: configFormatter("{width}x{height} {difficulty:Easy|Hard}"),
  },
  slide: {
    describeConfig: configFormatter("{width}x{height}, {solution-length-limit}", {
      "solution-length-limit": (value) =>
        Number(value) <= 0 ? "no move limit" : `max ${value} moves`,
    }),
    darkMode: {
      paletteSwaps: [
        [1, 2], // 3D
        [4, 5], // 3D dragging
        [7, 8], // main block 3D
        [10, 11], // main block 3D dragging
      ],
    },
  },
  sokoban: {
    describeConfig: configFormatter("{width}x{height}"),
    darkMode: {
      paletteSwaps: [[9, 10]], // 3D
    },
  },
  solo: {
    describeConfig: (config) => {
      const width = Number(config["columns-of-sub-blocks"]);
      const height = Number(config["rows-of-sub-blocks"]);
      const isJigsaw = Boolean(config.jigsaw);
      const isKiller = Boolean(config.killer);
      const isX = Boolean(config.x);
      const difficulty = [
        "Trivial",
        "Basic",
        "Intermediate",
        "Advanced",
        "Extreme",
        "Unreasonable",
      ][Number(config.difficulty)];
      const symmetry = [
        "no symmetry", // default for Killer
        "2-way rotation", // default for all but Killer
        "4-way rotation",
        "2-way mirror",
        "2-way diagonal mirror",
        "4-way mirror",
        "4-way diagonal mirror",
        "8-way mirror",
      ][Number(config.symmetry)];
      const hasDefaultSymmetry = config.symmetry === (isKiller ? 0 : 1);

      // Replicate preset titles
      const dimensions = isJigsaw ? `${width * height} Jigsaw` : `${width}x${height}`;
      const fullDifficulty = isKiller
        ? difficulty === "Trivial"
          ? "Killer" // "Killer" replaces "Trivial"
          : `Killer ${difficulty}`
        : difficulty;
      const symmetryDescription = hasDefaultSymmetry ? "" : `, ${symmetry}`;

      return `${dimensions} ${fullDifficulty}${isX ? " X" : ""}${symmetryDescription}`;
    },
    darkMode: {
      paletteOverrides: { 2: 0.8 }, // darken grid
    },
    extraCommands: [ALL_MARKS_COMMAND],
  },
  spokes: {
    describeConfig: configFormatter("{width}x{height} {difficulty:Easy|Tricky|Hard}"),
  },
  sticks: {
    describeConfig: configFormatter(
      "{width}x{height}{percentage-of-black-squares}{symmetry}",
      {
        // Default black squares is "20". Note value is 5-100, not 0.05-1.0.
        "percentage-of-black-squares": (value) =>
          String(value) === "20" ? "" : `, ${value}% black squares`,
        symmetry: [
          ", no symmetry",
          ", 2-way mirror",
          "", // default: 2-way rotational
          ", 4-way mirror",
          ", 4-way rotational",
        ],
      },
    ),
  },
  subsets: {
    // doesn't currently support custom configuration
  },
  tents: {
    describeConfig: configFormatter("{width}x{height} {difficulty:Easy|Tricky}"),
    darkMode: {
      paletteOverrides: { 2: 0.5 }, // darken grass
    },
  },
  towers: {
    describeConfig: configFormatter(
      "{grid-size}x{grid-size} {difficulty:Easy|Hard|Extreme|Unreasonable}",
    ),
    extraCommands: [ALL_MARKS_COMMAND],
  },
  tracks: {
    describeConfig: configFormatter(
      "{width}x{height} {difficulty:Easy|Tricky|Hard}{disallow-consecutive-1-clues:, allow adjacent 1’s|}",
    ),
  },
  twiddle: {
    describeConfig: (config) => {
      // Replicate preset titles
      const blockSize = Number(config["rotating-block-size"]);
      const blockSizeDescription =
        blockSize === 2
          ? "" // don't show default block size
          : `, rotating ${blockSize}x${blockSize} blocks`;

      const qualifiers: string[] = [];
      if (config["one-number-per-row"]) {
        qualifiers.push("rows only");
      }
      if (config["orientation-matters"]) {
        qualifiers.push("orientable");
      }
      if (!qualifiers.length && !blockSizeDescription) {
        // Only show "normal" if there's no other qualifier or block size
        qualifiers.push("normal");
      }
      const description = qualifiers.length ? ` ${qualifiers.join(", ")}` : "";
      const shuffles = Number(config["number-of-shuffling-moves"])
        ? `, ${Number(config["number-of-shuffling-moves"])} shuffles`
        : "";
      return `${config.width}x${config.height}${description}${blockSizeDescription}${shuffles}`;
    },
    darkMode: {
      paletteSwaps: [
        [2, 4], // highlight/lowlight 3D
        [3, 5], // gentle highlight/lowlight
        [6, 7], // highcursor/lowcursor
      ],
    },
  },
  undead: {
    describeConfig: configFormatter("{width}x{height} {difficulty:Easy|Normal|Tricky}"),
    extraCommands: [ALL_MARKS_COMMAND],
  },
  unequal: {
    describeConfig: configFormatter(
      "{mode:Unequal|Adjacent}: {size}x{size} {difficulty:Trivial|Easy|Tricky|Extreme|Recursive}",
    ),
    extraCommands: [ALL_MARKS_COMMAND, HINT_MARKS_COMMAND],
  },
  unruly: {
    describeConfig: configFormatter(
      "{width}x{height} {difficulty:Trivial|Easy|Normal}{unique-rows-and-columns:|, unique}",
    ),
    darkMode: {
      // preserve "black" and "white" plus their 3D effects
      paletteOverrides: { 3: false, 4: false, 5: false, 6: false, 7: false, 8: false },
    },
  },
  untangle: {
    describeConfig: configFormatter("{number-of-points} points"),
    paletteBgIndex: 1,
  },
};

/**
 * Factory for creating custom ConfigValues formatters from a template string
 * using this basic syntax:
 * - `{field}` substitutes the field value as a string.
 * - `{field:option 0|option 1|...}` coerces the field value to a number and
 *   substitutes the corresponding option string from a pipe-separated list.
 *   (If the value is out of range, the raw value is substituted as a string.)
 *   This syntax also works with boolean(ish) values: `{field:if false|if true}`.
 * - Anything outside {braces} is inserted verbatim.
 * - If the field does not appear in the config (or is undefined), it is not replaced.
 *
 * The customFormats argument can be used to provide additional per-field logic:
 * - If customFormats[field] is an array, it is treated as a list of options.
 *   (This may make the template string more readable for long options lists.)
 * - If customFormats[field] is a function, it is called with
 *   `(value, field, config: ConfigValues)` and should return a string.
 *
 * It is an error to specify both options and customFormats for the same field.
 */
function configFormatter(
  template: string,
  customFormats?: Record<
    string,
    | string[]
    | ((val: string | boolean | number, field: string, config: ConfigValues) => string)
  >,
) {
  return (config: ConfigValues): string =>
    template.replace(
      /\{(?<field>[a-z0-9-]+)(?::(?<options>[^}]*))?}/g,
      (orig, field: string, optionsList?: string): string => {
        const value = config[field];
        if (value === undefined) {
          return orig;
        }
        const custom = customFormats?.[field];
        if (custom !== undefined) {
          if (!import.meta.env.PROD && optionsList !== undefined) {
            throw new Error(`Field '${field}' has both options and customFormats`);
          }
          if (typeof custom === "function") {
            return custom(value, field, config);
          }
          return custom[Number(value)] ?? String(value);
        }
        if (optionsList !== undefined) {
          const options = optionsList.split("|");
          return options[Number(value)] ?? "";
        }
        return String(value);
      },
    );
}

/**
 * Convert ConfigValues value 0.0-1.0 to percentage string
 */
function percentage(value: string | boolean | number) {
  return `${Math.round(Number(value) * 100)}%`;
}
